import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ExternalLink, ShieldCheck, Sparkles, Loader2, GraduationCap, Heart } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { PageHero } from "@/components/PageHero";
import { RelatedLinks } from "@/components/RelatedLinks";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useCampaign } from "@/hooks/useCampaign";
import { SITE } from "@/data/site";

const MAIN_URL = process.env.REACT_APP_JOTFORM_KIDS_MAIN_URL || "";
const TASTER_URL = process.env.REACT_APP_JOTFORM_KIDS_URL || "";

const TABS = [
  {
    id: "main",
    label: "Full Registration",
    eyebrow: "Enrol",
    icon: GraduationCap,
    desc: "Sign your child up to a USDD Kids class, term or programme.",
    url: MAIN_URL,
    testid: "jotform-iframe",
  },
  {
    id: "taster",
    label: "£5 Taster",
    eyebrow: "Try a session",
    icon: Heart,
    desc: "Book the paid £5 USDD Kids Taster to try a session before committing to a full term.",
    url: TASTER_URL,
    testid: "jotform-taster-iframe",
  },
];

export default function KidsRegistrationPage() {
  const { intent } = useCampaign();
  const [active, setActive] = useState(intent === "taster" ? "taster" : "main");
  const [loaded, setLoaded] = useState({ main: false, taster: false });

  // React to URL changes (e.g. user navigates back/forward with a different ?intent)
  useEffect(() => {
    if (intent === "taster") setActive("taster");
    else if (intent === "main") setActive("main");
  }, [intent]);

  usePageMeta(
    "USDD Kids · Registration",
    "Register your child for USDD Kids — junior and youth street-dance training across London & Kent. Full enrolment or paid £5 Kids Taster, ages 6–17."
  );

  const tab = TABS.find((t) => t.id === active);

  return (
    <>
      <Header />
      <main>
        <PageHero
          overline="USDD Kids · Registration"
          title="Sign your kid"
          highlight="up."
          description="Junior and youth street-dance training across London & Kent. Confidence, culture and performance — taught the right way. Pick full registration or book the £5 Kids Taster below."
          ctaPrimary={{ label: "Talk to us first", to: "/contact" }}
          ctaSecondary={{ label: "About USDD Kids", to: "/kids" }}
          bg={SITE.kidsShow}
        />

        <section data-focus="kids" className="py-16 sm:py-20 bg-black">
          <div className="max-w-5xl mx-auto px-6 sm:px-8">
            <div className="flex items-center gap-3 mb-6">
              <Sparkles size={16} className="text-brand-yellow" />
              <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-brand-yellow">
                Registration forms
              </span>
            </div>

            {/* Tab switcher */}
            <div
              role="tablist"
              aria-label="Registration form type"
              data-testid="kids-reg-tabs"
              className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6"
            >
              {TABS.map((t) => {
                const isActive = active === t.id;
                return (
                  <button
                    key={t.id}
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => setActive(t.id)}
                    data-testid={`kids-reg-tab-${t.id}`}
                    className={`group text-left p-5 rounded-2xl border transition-all ${
                      isActive
                        ? "bg-gradient-to-br from-brand-purpleDeep/50 to-zinc-950 border-brand-yellow/60 glow-yellow"
                        : "bg-white/[0.03] border-white/10 hover:border-brand-yellow/40 hover:bg-white/[0.06]"
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <div
                        className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${
                          isActive ? "bg-brand-yellow" : "bg-brand-yellow/10 border border-brand-yellow/30"
                        }`}
                      >
                        <t.icon size={20} className={isActive ? "text-black" : "text-brand-yellow"} />
                      </div>
                      <div className="flex-1">
                        <div className="text-[10px] uppercase tracking-[0.3em] text-brand-yellow mb-1">{t.eyebrow}</div>
                        <div className="font-display text-xl text-white tracking-wide leading-tight">{t.label}</div>
                        <p className="mt-1 text-xs text-zinc-400 leading-relaxed">{t.desc}</p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Active form */}
            {tab.url ? (
              <div key={tab.id} className="relative rounded-3xl border border-white/10 overflow-hidden bg-white">
                {!loaded[tab.id] && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/80 z-10">
                    <Loader2 className="text-brand-yellow animate-spin" size={28} />
                  </div>
                )}
                <iframe
                  data-testid={tab.testid}
                  src={tab.url}
                  title={`USDD Kids ${tab.label}`}
                  width="100%"
                  height="720"
                  frameBorder="0"
                  onLoad={() => setLoaded((s) => ({ ...s, [tab.id]: true }))}
                  className="w-full"
                  style={{ minHeight: "720px", background: "white" }}
                />
                <div className="absolute top-3 right-3 z-20">
                  <a
                    href={tab.url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/70 backdrop-blur border border-white/15 text-[10px] uppercase tracking-[0.25em] text-zinc-300 hover:text-brand-yellow"
                  >
                    Open form <ExternalLink size={11} />
                  </a>
                </div>
              </div>
            ) : (
              <div
                data-testid="jotform-placeholder"
                className="relative rounded-3xl border border-brand-yellow/30 bg-gradient-to-br from-brand-purpleDeep/40 to-zinc-950 p-8 sm:p-12 overflow-hidden"
              >
                <div className="absolute -top-12 -right-12 w-56 h-56 rounded-full bg-brand-yellow/10 blur-3xl" />
                <div className="text-[10px] uppercase tracking-[0.3em] text-brand-yellow mb-2">
                  Form connecting soon
                </div>
                <h2 className="font-display text-3xl sm:text-4xl text-white tracking-wide leading-tight">
                  We're wiring this one up.
                </h2>
                <p className="mt-3 text-sm text-zinc-400 max-w-xl">
                  In the meantime, drop us a message and we'll register your
                  child manually within 24 hours.
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <Link
                    to="/contact"
                    data-testid="kids-registration-contact-btn"
                    className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-brand-yellow text-black font-black uppercase text-xs tracking-[0.2em] hover:bg-brand-yellowHover transition-all"
                  >
                    Register via message <ArrowRight size={14} />
                  </Link>
                </div>
              </div>
            )}

            <div className="mt-6 flex items-center justify-center gap-2 text-[10px] uppercase tracking-[0.25em] text-zinc-500">
              <ShieldCheck size={12} /> Your data is kept private. We never share registrations.
            </div>
          </div>
        </section>

        <RelatedLinks
          title="More about USDD Kids"
          items={[
            { tag: "Programme", label: "USDD Kids", desc: "Confidence, culture & performance for ages 6–17", to: "/kids" },
            { tag: "Pricing", label: "Kids term & drop-ins", desc: "See current prices and offers", to: "/pricing" },
            { tag: "Schedule", label: "Live timetable", desc: "Book a class via Acuity", to: "/schedule" },
          ]}
        />
      </main>
      <Footer />
    </>
  );
}
